import './App.scss';
import '../../variables.css';
import '../../theme.css';
import React, { useState, useEffect, useMemo, useContext, Fragment } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import NavBar from 'components/NavBar/NavBar';
import { AuthService, useAuthService } from 'services/AuthService/AuthService';
import { PushService, usePushService } from 'services/PushService/PushService';
import { Protected } from './Protected';
import i18n from 'localization/i18n';
import { I18nextProvider } from 'react-i18next';
import { getConfig } from 'config';
import { useTabletOrMobile } from 'utils/hooks/useTabletOrMobile';
import classNames from 'classnames';
import _ from 'lodash';
import AppRoutesAndMenu from './AppRoutesAndMenu.js';
import { QueryCache, QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import createQueryRequest from 'services/createQueryRequest';
import useBreadcrumbs from 'utils/hooks/useBreadcrumbs';
import { useTranslation } from 'localization/useTranslation';
import { v4 as uuidv4 } from 'uuid';
import { Alert, AlertGroup, AlertVariant } from '@patternfly/react-core';
import AlertContext from 'services/AlertContext';
import { Settings } from 'luxon';


// import React, { Suspense, lazy } from 'react';
// import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

// TODO: https://reactjs.org/docs/code-splitting.html#route-based-code-splitting
// const Home = lazy(() => import('./routes/Home'));
// const About = lazy(() => import('./routes/About'));

// const App = () => (
//   <Router>
//     <Suspense fallback={<div>Loading...</div>}>
//       <Switch>
//         <Route exact path="/" component={Home}/>
//         <Route path="/about" component={About}/>
//       </Switch>
//     </Suspense>
//   </Router>
// );

const RouteLayer = () => {
  const authService = useAuthService();
  Settings.defaultLocale = 'en-US';
  return (
    <AuthService.Provider value={authService}>
      <Router>
        <PushService.Provider value={usePushService()}>
          <InternalApp />
        </PushService.Provider>
      </Router>
    </AuthService.Provider>
  );
};

const createComponent = route => {
  const Component = route.component;
  if (route.protected) {
    return (props) => <Protected><Component {...props}/></Protected>;
  }
  return route.component;
};


const routesFromMatch = (match, location, routes) => {
  const search = location.search;

  return _.reduce(routes, (result, route) => {
    const paths = _.isArray(route.path) ? route.path : [route.path];
    const matchPaths = _.filter(paths, path => {
      if (_.isObject(path)) {
        return match.path.startsWith(path.path);
      }
      return match.path.startsWith(path);
    });
    if (matchPaths.length > 0 && matchPaths[0] !== '/') {
      const path = _.isObject(matchPaths[0]) ? matchPaths[0].path : matchPaths[0];
      result.push({
        ...matchPaths[0],
        location: {
          pathname: replacePath(path, match.params),
          search: location.search
        },
        params: match.params,
        search
      });
    }
    return result;
  }, []);
};

const replacePath = (path, params) => {
  return _.reduce(params, (result, value, key) => {
    return result?.replace(`:${key}`, value);
  }, path);
};

const BreadcrumbCreator = ({ crumbs, children }) => {
  const breadcrumbs = useBreadcrumbs(crumbs);
  return children(breadcrumbs);
};

const createRoute = route => {
  const paths = _.isArray(route.path) ? route.path : [route.path];
  return _.map(paths, path => {
    if (_.isObject(path)) {
      const Component = createComponent(route);
      return (<Route path={path.path} key={path.path} exact={route.exact} render={props => {
        const crumbs = routesFromMatch(props.match, props.location, routes);
        return (
          <BreadcrumbCreator crumbs={crumbs}>{
            (breadcrumbs) => <Component {...props} {...path.props} breadcrumbs={breadcrumbs}/>
          }</BreadcrumbCreator>
        );
      }}/>);
    }
    const Component = createComponent(route);
    return <Route path={path} key={path} exact={route.exact}><Component/></Route>;
  });
};

const menu = _.filter(AppRoutesAndMenu, menuItem => menuItem.name != null || menuItem.type != null);
const routes = _.filter(AppRoutesAndMenu, item => item.path != null && item.component != null);

const prefetchCommonData = async (queryClient, api) => {
  // The results of this query will be cached like a normal query
  await queryClient.prefetchQuery({
    queryKey: ['treeRoots'],
    queryFn: createQueryRequest(api.core.getTrees)
  });
};

const InternalApp = () => {
  return (
    <Fragment>
      <div className="root">
        <I18nextProvider i18n={i18n}>
          <AppNavBarContent />
        </I18nextProvider>
      </div>
      <ReactQueryDevtools initialIsOpen={false} />
    </Fragment>
  );
};

const AppNavBarContent = () => {
  const queryClient = useQueryClient();
  const [sideBarIsCollapsed, setSideBarCollapse] = useState();
  const T = useTranslation();
  useEffect(() => {
    document.title = getConfig().metadata.title;
  });

  const authService = useContext(AuthService);
  const isTabletOrMobile = useTabletOrMobile();
  const routeComponents = useMemo(() => _.flatMap(routes, createRoute), []);
  const userData = authService.getUserData();
  const filteredMenu = useMemo(() => {
    return _.reject(menu, menuItem => menuItem.isEnabled != null && !menuItem.isEnabled(userData));
  }, [userData]);

  useEffect(() => {
    if (authService.isLoggedIn) {
      prefetchCommonData(queryClient, authService.api);
    } else {
      queryClient.removeQueries();
    }
  }, [authService.api, authService.isLoggedIn, queryClient]);
  const [alerts, setAlerts] = useState([]);

  const DEFAULT_ALERT_TIMEOUT = 1000; // ms

  const alertService = useMemo(() => {
    return {
      addAlert: (alert) => {
        setAlerts(oldAlerts => {
          const key = uuidv4();
          return [
            ...oldAlerts,
            <Alert
              key={key}
              {...alert}
              onTimeout={() => {
                setAlerts(oldAlerts => oldAlerts.filter(item => item.key !== key));
              }}
              onClose={() => {
                setAlerts(oldAlerts => oldAlerts.filter(item => item.key !== key));
              }}
            />
          ];
        });
      },
      addSuccessAlert: (title, options) => {
        alertService.addAlert({ variant: AlertVariant.success, title, timeout: options?.timeout ?? DEFAULT_ALERT_TIMEOUT });
      },
      addErrorAlert: (title, options) => {
        alertService.addAlert({ variant: AlertVariant.danger, title, timeout: options?.timeout ?? DEFAULT_ALERT_TIMEOUT });
      }
    };
  }, []);

  errorMessageCallback = (unusedError, query) => {
    if (query.meta?.errorMessage) {
      alertService.addErrorAlert(T.localize(query.meta.errorMessage));
    }
  };

  successMessageCallback = (unusedData, query) => {
    if (query.meta?.successMessage) {
      alertService.addSuccessAlert(T.localize(query.meta.successMessage));
    }
  };

  return (
    <AlertContext.Provider value={alertService}>
      <AlertGroup isToast isLiveRegion>{ alerts }</AlertGroup>
      <NavBar sideBarCollapseCallback={setSideBarCollapse} menu={filteredMenu}>
        <div
          className={classNames('app-normal',
            {
              'app-mobile-sidebar-collapsed': sideBarIsCollapsed && isTabletOrMobile,
              'content-sidebar-hidden': sideBarIsCollapsed && !isTabletOrMobile,
              'content-sidebar-expanded': !sideBarIsCollapsed
            })}
          id="content">
          <div className="app-body">
            <Switch>
              { routeComponents }
            </Switch>
          </div>
        </div>
      </NavBar>
    </AlertContext.Provider>
  );
};

// Not the pretties, but it works
let errorMessageCallback = null;
let successMessageCallback = null;
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (...args) => {
      if (errorMessageCallback) {
        try {
          errorMessageCallback(...args);
        } catch (error) {
          console.error(error);
        }
      }
    },
    onSuccess: (...args) => {
      if (successMessageCallback) {
        try {
          successMessageCallback(...args);
        } catch (error) {
          console.error(error);
        }
      }
    }
  })
});

const App = ({ pca }) => {
  return (
    <MsalProvider instance={pca}>
      <QueryClientProvider client={queryClient}>
        <RouteLayer />
      </QueryClientProvider>
    </MsalProvider>);
};

export default App;
